import Button from "@mui/material/Button";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import { getErrorMessageFromUnknown } from "_shared/_errors/api-error-message";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import AccessRequest, { pendingRequestFor } from "permissions/access-requests/access-request";
import {
  AccessRequestProblem,
  accessRequestProblemCode,
  useGetMyAccessRequestsQuery,
  useRequestAccessMutation,
} from "permissions/access-requests/_api/access-requests-api";
import type { RequestableAccess } from "permissions/access-requests/requestable-access";
import { useMemo } from "react";

export const ACCESS_REQUESTED_MESSAGE = "Access requested. You will be told when it is decided.";
export const ACCESS_REQUEST_FAILED_MESSAGE = "The access could not be requested. Try again.";

/** "Requested on Oct 15, 2026". */
export function requestedOnLabel(request: AccessRequest): string {
  return `Requested on ${request.createdDate.toString("MMM D, YYYY")}`;
}

/**
 * The reader's request for this access that is still waiting, if there is one, for a page that words
 * its helper text around it. The same query the button reads, so it is asked for once.
 */
export function usePendingAccessRequest(access: RequestableAccess): AccessRequest | undefined {
  const mine = useGetMyAccessRequestsQuery();
  const requests = useMemo(() => AccessRequest.fromJSONArray(mine.data), [mine.data]);
  return pendingRequestFor(requests, access);
}

type RequestAccessButtonProps = {
  /** The access to ask for, in the shape a grant of it has. */
  resource: RequestableAccess;
  /** What the reader is trying to do, such as "Upload files to Patent filing". The grantor reads it. */
  context: string;
  /** The page the reader is asking from, so a grantor can see what it was about. */
  returnPath?: string;
  /** A link inside a sentence of helper text, or a button of its own. */
  variant: "link" | "button";
  /** The link's or the button's words. "Request access" when not given. */
  label?: string;
};

/**
 * Asks the people in the reader's organization who may grant an access for it (#6596).
 *
 * It reads *Request access* until the reader asks, and *Requested on …* while their request waits,
 * whichever page it was asked from: the API answers a second request for the same access with the
 * first, and this reads the reader's own requests to say so before they click. Once the access is
 * granted the control it stands in for is enabled, so the caller stops drawing this; the caller
 * decides that with a capability on `useSessionUser()`, the same one that disabled the control.
 *
 * A request the API refuses says why in a snackbar, in the API's words: the reader already holds the
 * access, or nobody in their organization may grant it.
 */
export default function RequestAccessButton(props: Readonly<RequestAccessButtonProps>) {
  const { resource, context, returnPath, variant } = props;
  const label = props.label ?? "Request access";
  const pending = usePendingAccessRequest(resource);
  const [requestAccess, requesting] = useRequestAccessMutation();

  async function send() {
    const outcome = await requestAccess({ resource, context, returnPath })
      .unwrap()
      .then(() => ({ error: undefined as unknown, failed: false }))
      .catch((error: unknown) => ({ error, failed: true }));
    if (!outcome.failed) {
      enqueueSnackbar(ACCESS_REQUESTED_MESSAGE, { variant: "success", role: "status" });
      return;
    }
    const code = accessRequestProblemCode(outcome.error);
    const message = getErrorMessageFromUnknown(outcome.error) ?? ACCESS_REQUEST_FAILED_MESSAGE;
    const answered = code === AccessRequestProblem.AlreadyHeld || code === AccessRequestProblem.NoGrantor;
    enqueueSnackbar(message, { variant: answered ? "info" : "error" });
  }

  if (pending) {
    if (variant === "button") {
      return (
        <Button size="small" variant="outlined" disabled>
          {requestedOnLabel(pending)}
        </Button>
      );
    }
    return (
      <Typography component="span" variant="inherit" sx={{ fontWeight: 500 }}>
        {requestedOnLabel(pending)}
      </Typography>
    );
  }

  if (variant === "button") {
    return (
      <Button size="small" variant="outlined" disabled={requesting.isLoading} onClick={() => void send()}>
        {label}
      </Button>
    );
  }
  return (
    <Link
      component="button"
      type="button"
      variant="inherit"
      disabled={requesting.isLoading}
      onClick={() => void send()}
      sx={{ verticalAlign: "baseline" }}
    >
      {label}
    </Link>
  );
}
